/**
 * @fileoverview Gather Phase Helper Functions
 *
 * Purpose: Utility functions for the Gather Information phase UI.
 * File icons, size formatting, date formatting.
 *
 * Used by:
 * - DocumentDetail, library/LibraryView
 */

import { FileText, FileSpreadsheet, FileImage, File, Mail } from "lucide-react";
import type { LucideIcon } from "lucide-react";

/**
 * Returns an appropriate Lucide icon component for a MIME type / file extension.
 *
 * @param fileType - The MIME type string
 * @returns A Lucide icon component
 */
export function getFileIcon(fileType: string): LucideIcon {
  if (fileType.includes("pdf")) return FileText;
  if (
    fileType.includes("spreadsheet") ||
    fileType.includes("excel") ||
    fileType.includes("csv")
  )
    return FileSpreadsheet;
  if (fileType.includes("image")) return FileImage;
  if (fileType.includes("message") || fileType.includes("email")) return Mail;
  return File;
}

/**
 * Formats file size in human-readable format (B, KB, MB).
 *
 * @param bytes - File size in bytes
 * @returns Formatted string like "1.5 MB"
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Formats a timestamp to a relative or absolute date string.
 *
 * @param timestamp - Unix timestamp in milliseconds
 * @returns Relative date ("Today", "Yesterday") or formatted date
 */
export function formatDate(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}
